// SKILLSMITH-OPPORTUNITIES (founder, 2026-10-30): selling a helper is a paid flow.
//
//   request (a client's "Ask for a helper", or a Skillsmith opportunity)
//   → a platform super admin sends a quote: the helper's name, a one-off and/or a monthly price
//   → the company's owner or admin sees it in the Inbox and on Billing and Approves or Declines
//   → on approval: the one-off part is invoiced and charged through the billing path (until
//     one-time charges ship, billing/flags.ts, the invoice is recorded and the charge waits), the
//     monthly part is added to the subscription as "Helper: <name>", and the build starts
//   → the super admin marks it Added once delivered.
//
// The client's routes (`manager`: owners and admins read; only the company's own owner or admin
// decides — an agency operator never approves with a client's money) and the super admin's
// (`/v1/operator/helper-offers/{company_id}…`, 404 to anyone but a platform super admin holding a
// live operator grant on the company).
import { z } from "zod";
import { Currency, HelperChargeStatus, HelperOfferStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

export const HelperOffer = z
  .object({
    id: Uuid,
    de: z.object({ membership_id: Uuid, name: z.string() }).nullable(),
    helper_name: z.string(),
    description: z.string().nullable(),
    one_off_minor: z.int().min(0),
    monthly_minor: z.int().min(0),
    currency: Currency,
    status: HelperOfferStatus,
    /** The client's "Ask for a helper" request it answers, if any. */
    request_ref: z.string().nullable(),
    decided_at: Timestamp.nullable(),
    charge_status: HelperChargeStatus,
    /** The invoice recorded on approval (number and what it bills), if any. */
    invoice: z
      .object({ number: z.string(), amount_minor: z.int().min(0), at: Timestamp })
      .nullable(),
    /** The subscription line it adds while building or added: "Helper: <name>", monthly. */
    subscription_line: z.object({ label: z.string(), monthly_minor: z.int().min(0) }).nullable(),
    added_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "HelperOffer" });

export const HelperOfferList = z
  .object({
    data: z.array(HelperOffer),
    /** Whether the reader may Approve / Decline (the company's own owner or admin). */
    can_decide: z.boolean(),
  })
  .meta({ id: "HelperOfferList" });

export const HelperOfferDecide = z
  .strictObject({ decision: z.enum(["approve", "decline"]) })
  .meta({ id: "HelperOfferDecide" });

export const HelperOfferCreate = z
  .strictObject({
    helper_name: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(2000).optional(),
    de_membership_id: Uuid.optional(),
    one_off_minor: z.int().min(0).max(100_000_000_00),
    monthly_minor: z.int().min(0).max(100_000_000_00),
    currency: Currency.optional(),
    /** The Skillsmith opportunity it answers (moves to Offered). */
    opportunity_id: Uuid.optional(),
    /** The client's "Ask for a helper" request it answers (moves to Quoted). */
    request_ref: z.string().trim().min(1).max(100).optional(),
  })
  .refine((b) => b.one_off_minor + b.monthly_minor > 0, {
    message: "a quote has a one-off or a monthly price",
    path: ["one_off_minor"],
  })
  .meta({ id: "HelperOfferCreate" });

export const HelperOfferStatusPut = z
  .strictObject({ status: z.enum(["added", "canceled"]) })
  .meta({ id: "HelperOfferStatusPut" });

const OfferParams = z.strictObject({ co: Uuid, offer_id: Uuid });
const OperatorParams = z.object({ company_id: Uuid });
const OperatorOfferParams = z.object({ company_id: Uuid, offer_id: Uuid });

const billing = { feature: "core", tag: "billing" } as const;
const operator = { feature: "core", tag: "operator", access: "operator" } as const;

export const helperOfferRoutes = {
  "billing.helperOffers.list": defineRoute({
    ...billing,
    id: "billing.helperOffers.list",
    method: "get",
    path: "/v1/companies/{co}/billing/helper-offers",
    summary: "Helper quotes sent to the company, newest first (owners and admins)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: HelperOfferList },
    errors: [],
    emits: [],
  }),
  "billing.helperOffers.decide": defineRoute({
    ...billing,
    id: "billing.helperOffers.decide",
    method: "post",
    path: "/v1/companies/{co}/billing/helper-offers/{offer_id}/decide",
    summary:
      "Approve (invoiced and charged, its monthly line added, the build starts) or decline a helper quote (the company's own owner or admin)",
    access: "manager",
    params: OfferParams,
    body: HelperOfferDecide,
    idempotency: "optional",
    response: { status: 200, schema: HelperOffer },
    errors: ["conflict"],
    emits: ["helper_offer.changed"],
  }),
  "operator.helperOffers.list": defineRoute({
    ...operator,
    id: "operator.helperOffers.list",
    method: "get",
    path: "/v1/operator/helper-offers/{company_id}",
    summary: "The helper quotes sent to one company (super admins only)",
    params: OperatorParams,
    response: { status: 200, schema: z.object({ data: z.array(HelperOffer) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.helperOffers.create": defineRoute({
    ...operator,
    id: "operator.helperOffers.create",
    method: "post",
    path: "/v1/operator/helper-offers/{company_id}",
    summary:
      "Send the company a helper quote (from an opportunity or a request): its owners and admins Approve or Decline it (super admins only)",
    params: OperatorParams,
    body: HelperOfferCreate,
    idempotency: "optional",
    response: { status: 201, schema: HelperOffer },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: ["helper_offer.changed"],
  }),
  "operator.helperOffers.status": defineRoute({
    ...operator,
    id: "operator.helperOffers.status",
    method: "post",
    path: "/v1/operator/helper-offers/{company_id}/{offer_id}/status",
    summary:
      "Mark an approved helper Added once delivered, or cancel a quote still open (super admins only)",
    params: OperatorOfferParams,
    body: HelperOfferStatusPut,
    response: { status: 200, schema: HelperOffer },
    errors: ["not_found", "conflict"],
    emits: ["helper_offer.changed"],
  }),
} as const;
